/**
 * PATH: packages/email-hub/src/feature/hooks/useFindInThread.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USEFINDINTHREAD-001
 * PURPOSE: React wrapper for find-in-thread (G5): memoises the pure
 *          per-message match computation over the open thread so
 *          EmailThread can auto-expand collapsed messages with hits and
 *          force quoted-history sections open when the hit is inside them.
 * WHY:     The DOM highlighter (@quotech/ui useInPageHighlight) can only
 *          reach RENDERED text. This hook is the "what would match if it
 *          were rendered" side, computed from the raw bodies.
 * FLOW: (messages, query) ? useMemo(computeThreadFindState) ? ThreadFindState.
 * DEPENDENCIES:
 *   - ../lib/findInThread (the pure logic; unit-tested there).
 * RELATED:
 *   - components/EmailThread.tsx (consumer)
 */

import { useMemo } from 'react'
import {
  computeThreadFindState,
  type FindableMessage,
  type ThreadFindState,
} from '../lib/findInThread'

/**
 * Per-message find matches for the open thread. Blank/undefined query ?
 * empty state (nothing force-expands). Recomputes only when the message
 * list identity or the query changes.
 */
export function useFindInThread(
  messages: readonly FindableMessage[],
  query: string | undefined,
): ThreadFindState {
  return useMemo(
    () => computeThreadFindState(messages, query ?? ''),
    [messages, query],
  )
}
